import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useDragControls, useMotionValue, animate } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ThumbsDown,
  ThumbsUp,
  Bookmark,
  BedDouble,
  Bath,
  Ruler,
  PawPrint,
  WashingMachine,
  Car,
  Cigarette,
  CigaretteOff,
  CalendarDays,
  Zap,
} from "lucide-react";
import { format } from "date-fns";
import ButtonIcon from "@/components/buttons/button_icon";
import { priceParts } from "@/lib/pricing";
import { collectAmenities, iconFor, labelFor, plainDescription } from "@/lib/amenities";
import { useAuth } from "@/context/AuthContext";
import { useSaves } from "@/context/SavesContext";
import BadgeVerified from "@/components/badges/badge_verified";
import { ListerBadge } from "@/components/badges/badge_realtor";

// Profile popup v2 — stable by construction:
//
// - The sheet itself NEVER translates while browsing. There is no whole-sheet
//   horizontal drag; like / nope from inside are the two buttons only
//   (collapse first, then the deck swipe — gates still veto).
// - Drag-down-to-dismiss works ONLY from the grabber pill at the very top
//   (dragListener is off; the handle routes its gesture into the sheet's
//   drag controls). Dragging anywhere else never moves the sheet. Chevron
//   and ESC still close.
// - The photo pane is a real swipeable carousel: horizontal drag / flick
//   with snap physics (spring 400/40, commit at 30% width or a 400 px/s
//   flick), animated progress segments, tap zones as a secondary affordance,
//   adjacent images preloaded, video slide first and paused when not active.
// - Content scrolls in a fixed white sheet under the pane; the action bar is
//   pinned to the bottom (backdrop blur) with Pass / Save / Like.

const SHEET_EASE = [0.32, 0.72, 0, 1];
const SNAP_SPRING = { type: "spring", stiffness: 320, damping: 36, restDelta: 0.5 };

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-night-tint px-3 py-1.5 text-xs font-semibold text-night-text">
      {Icon && <Icon size={14} className="shrink-0 text-brand-primary" />}
      {children}
    </span>
  );
}

// One carousel slide. Videos pause + rewind the moment they are not the
// active slide; nothing inside a slide ever intercepts the pane's drag.
function MediaSlide({ item, active, onProgress }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      v.play?.().catch(() => {});
    } else {
      v.pause?.();
      try {
        // WHY 0.1 and not 0: frame zero is black in most encodes, and a
        // rewind to 0 also defeats the #t=0.1 src fragment below.
        v.currentTime = 0.1;
      } catch {
        /* not seekable yet */
      }
    }
  }, [active]);

  return (
    <div className="h-full w-full shrink-0">
      {item.type === "video" ? (
        <video
          ref={videoRef}
          src={item.poster ? item.src : `${item.src}#t=0.1`}
          preload="auto"
          muted
          playsInline
          loop
          poster={item.poster}
          onTimeUpdate={(e) =>
            active && e.target.duration && onProgress(e.target.currentTime / e.target.duration)
          }
          className="pointer-events-none h-full w-full object-cover"
        />
      ) : (
        <img
          src={item.src}
          alt=""
          draggable={false}
          decoding="async"
          className="pointer-events-none h-full w-full select-none object-cover"
        />
      )}
    </div>
  );
}

// The generic shell (stable sheet + snap carousel + docked actions), shared
// by the listing expand below and the renter expand in renter_browse.
export function ExpandShell({
  onClose,
  onAction,
  media,
  initialSlide = 0,
  mediaOverlay = null,
  save = null, // { saved, onToggle } — renter popups pass nothing
  children,
  testId = "expand",
}) {
  const count = media.length;
  const [slide, setSlide] = useState(() => Math.min(Math.max(initialSlide, 0), Math.max(count - 1, 0)));
  const [videoProgress, setVideoProgress] = useState(0);
  const closingRef = useRef(false);
  const pressRef = useRef(null);
  // Two independent gestures, both explicit: the handle drags the sheet down,
  // the photo pane (and its tap zones) drags the carousel strip sideways.
  const sheetControls = useDragControls();
  const stripControls = useDragControls();
  const paneRef = useRef(null);
  const [paneW, setPaneW] = useState(0);
  const x = useMotionValue(0); // carousel strip position: snaps to -slide * paneW

  useLayoutEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const measure = () => setPaneW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Snap physics: the strip springs to the active slide (stiffness 400,
  // damping 40). First measurement positions it instantly.
  const firstSnap = useRef(true);
  useLayoutEffect(() => {
    if (!paneW) return;
    if (firstSnap.current) {
      firstSnap.current = false;
      x.set(-slide * paneW);
      return;
    }
    const ctrl = animate(x, -slide * paneW, SNAP_SPRING);
    return () => ctrl.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide, paneW]);

  // Preload the neighbours so swiping never shows a white frame.
  useEffect(() => {
    [slide - 1, slide + 1].forEach((i) => {
      const m = media[i];
      if (m?.type === "photo") {
        const img = new Image();
        img.src = m.src;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide]);

  // WHY: with an empty media list the old `Math.min(count - 1, ...)` could
  // produce -1; clamp into [0, count - 1] with a floor of 0 instead.
  const clampSlide = (i) => Math.min(Math.max(count - 1, 0), Math.max(0, i));

  // WHY: if the media list changes while the popup is open the remembered
  // slide can point past the new end (or below 0); pull it back into range.
  useEffect(() => {
    setSlide((s) => clampSlide(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  function goTo(next) {
    const clamped = clampSlide(next);
    if (clamped !== slide) {
      setVideoProgress(0);
      setSlide(clamped);
    }
  }

  // Commit rule: 30% of the pane width, or a 400 px/s flick.
  function onStripDragEnd(_e, info) {
    const { offset, velocity } = info;
    if (offset.x < -paneW * 0.3 || velocity.x < -400) goTo(slide + 1);
    else if (offset.x > paneW * 0.3 || velocity.x > 400) goTo(slide - 1);
    else animate(x, -slide * paneW, SNAP_SPRING);
  }

  function requestClose() {
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
  }

  // Buttons: collapse the popup first (~0.2s), THEN run the deck swipe.
  function pressAction(action) {
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
    setTimeout(() => onAction?.(action), 200);
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Tap zones stay as a secondary affordance: a real tap (not a drag) moves
  // the carousel one photo; a horizontal pull on them drives the strip drag.
  function zoneDown(e) {
    pressRef.current = { x: e.clientX, y: e.clientY };
    if (count > 1) stripControls.start(e);
  }
  function zoneClick(delta) {
    return (e) => {
      const p = pressRef.current;
      pressRef.current = null;
      if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 8) return;
      goTo(slide + delta);
    };
  }

  return (
    <motion.div
      data-testid={testId}
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 sm:items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Outer wrapper does the SLIDE up/down. It's kept separate from the
          sheet's own drag-to-dismiss so the entrance transform and the drag
          transform never write the same value at once — that conflict was the
          glitch on the way up. Slide = one gliding, slightly-overdamped spring
          (no bounce, no abrupt slow-down); willChange keeps it on its own GPU
          layer at 60fps. */}
      <motion.div
        className="h-[100dvh] w-full max-w-[560px] sm:h-[calc(100vh-2rem)]"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        style={{ willChange: "transform" }}
        transition={{ type: "spring", stiffness: 200, damping: 32, mass: 1.2, restDelta: 0.5 }}
      >
      <motion.div
        data-testid="expand-sheet"
        className="relative flex h-full w-full flex-col overflow-hidden rounded-t-3xl bg-night-surface sm:rounded-3xl"
        drag="y"
        dragListener={false}
        dragControls={sheetControls}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.9 }}
        dragMomentum={false}
        onDragEnd={(_e, info) => {
          if (info.offset.y > 100 || info.velocity.y > 500) requestClose();
        }}
      >
        {/* ---- Photo pane (~55vh): the swipeable carousel ---- */}
        <div ref={paneRef} className="relative h-[55vh] max-h-[62%] w-full shrink-0 overflow-hidden bg-neutral-900">
          <motion.div
            data-testid="expand-photos"
            className="flex h-full w-full"
            style={{ x, touchAction: "pan-y", willChange: "transform" }}
            drag={count > 1 ? "x" : false}
            dragControls={stripControls}
            dragConstraints={{ left: -Math.max(0, count - 1) * paneW, right: 0 }}
            dragElastic={0.15}
            dragMomentum={false}
            onDragEnd={count > 1 ? onStripDragEnd : undefined}
          >
            {media.map((m, i) => (
              <MediaSlide key={i} item={m} active={i === slide} onProgress={setVideoProgress} />
            ))}
          </motion.div>

          {/* Top legibility gradient only (for the progress bars / handle /
              close). No bottom scrim: the price/title moved below the photo so
              the image shows fully. */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/50 to-transparent" />

          {/* Stories-style progress segments — the active one animates in */}
          {count > 1 || media[0]?.type === "video" ? (
            <div data-testid="expand-progress" className="pointer-events-none absolute inset-x-3 top-8 z-10 flex gap-1">
              {media.map((m, i) => (
                <span key={i} className="h-1 flex-1 overflow-hidden rounded-full bg-white/35">
                  <span
                    className="block h-full rounded-full bg-white transition-[width] duration-300 ease-out"
                    style={{
                      width:
                        i < slide
                          ? "100%"
                          : i === slide
                            ? m.type === "video"
                              ? `${Math.round(videoProgress * 100)}%`
                              : "100%"
                            : "0%",
                    }}
                  />
                </span>
              ))}
            </div>
          ) : null}

          {/* Secondary tap zones (left = previous, right = next). A sideways
              pull on them feeds the carousel drag, never the sheet. */}
          <button
            type="button"
            aria-label="Previous photo"
            data-testid="expand-zone-left"
            onPointerDown={zoneDown}
            onClick={zoneClick(-1)}
            className="absolute inset-y-0 left-0 z-[5] w-1/2 cursor-default touch-pan-y focus:outline-none"
          />
          <button
            type="button"
            aria-label="Next photo"
            data-testid="expand-zone-right"
            onPointerDown={zoneDown}
            onClick={zoneClick(1)}
            className="absolute inset-y-0 right-0 z-[5] w-1/2 cursor-default touch-pan-y focus:outline-none"
          />

          {/* Price / title overlay pinned to the pane — never moves */}
          {mediaOverlay && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-5 text-white">{mediaOverlay}</div>
          )}

          {/* Grabber handle — the ONLY place a drag can dismiss the sheet */}
          <div
            data-testid="expand-handle"
            onPointerDown={(e) => sheetControls.start(e)}
            className="absolute inset-x-0 top-0 z-30 flex h-7 cursor-grab touch-none items-start justify-center pt-2.5 active:cursor-grabbing"
          >
            <span className="h-1.5 w-10 rounded-full bg-white/80 shadow-sm" />
          </div>

          {/* Close: down chevron, top right of the photo */}
          <button
            type="button"
            aria-label="Close profile"
            data-testid="expand-close"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={requestClose}
            className="absolute right-3 top-11 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow-ambient transition hover:bg-white"
          >
            <ChevronDown size={24} />
          </button>
        </div>

        {/* ---- Scrollable info column (dragging here never moves the sheet) ---- */}
        <div data-testid="expand-content" className="min-h-0 flex-1 overflow-y-auto px-5 pb-28 pt-4">
          {children}
        </div>

        {/* ---- Pinned action bar: Pass / Save / Like ---- */}
        <div className="absolute inset-x-0 bottom-0 z-20 border-t border-night-line bg-night-surface/80 backdrop-blur">
          <div className="flex items-center justify-center gap-5 pb-[max(env(safe-area-inset-bottom),0.55rem)] pt-2">
            <ButtonIcon
              icon={ThumbsDown}
              label="Pass"
              onClick={() => pressAction("pass")}
              size={22}
              strokeWidth={1.6}
              className="h-12 w-12 text-pass"
            />
            {save && (
              <ButtonIcon
                icon={Bookmark}
                label="Save this home"
                aria-pressed={save.saved}
                data-testid="expand-save"
                onClick={save.onToggle}
                size={19}
                strokeWidth={1.6}
                className="h-11 w-11 text-brand-primary"
                iconClassName={save.saved ? "fill-brand-primary" : ""}
              />
            )}
            <ButtonIcon
              icon={ThumbsUp}
              label="Like"
              onClick={() => pressAction("like")}
              size={22}
              strokeWidth={1.6}
              className="h-12 w-12 text-like"
            />
          </div>
        </div>
      </motion.div>
      </motion.div>
    </motion.div>
  );
}

function fmtDate(value) {
  try {
    return format(new Date(value), "MMM d");
  } catch {
    return String(value || "soon");
  }
}

// The listing flavor: price + title over the photo, icon info chips,
// description, amenities and a small text link to the full listing page.
export default function ListingExpand({ open, listing, initialPhoto = 0, onClose, onAction, onRequireAuth }) {
  return (
    <AnimatePresence>
      {open && listing && (
        <ListingExpandInner
          key={listing.id}
          listing={listing}
          initialPhoto={initialPhoto}
          onClose={onClose}
          onAction={onAction}
          onRequireAuth={onRequireAuth}
        />
      )}
    </AnimatePresence>
  );
}

function ListingExpandInner({ listing, initialPhoto, onClose, onAction, onRequireAuth }) {
  const parts = priceParts(listing);
  const { user } = useAuth();
  const { ids: savedIds, toggle: toggleSave } = useSaves();
  const photos = listing.photos || [];
  // A listing video (if present) is always the FIRST slide, autoplaying muted.
  const media = [
    ...(listing.video_url ? [{ type: "video", src: listing.video_url, poster: listing.video_poster }] : []),
    ...photos.map((src) => ({ type: "photo", src })),
  ];
  const initialSlide = listing.video_url ? (initialPhoto === 0 ? 0 : initialPhoto + 1) : initialPhoto;
  const amenities = listing.amenities || [];
  const hasAmenity = (re) => amenities.some((a) => re.test(a));

  // The bookmark SAVES the home — it never closes the popup or swipes the
  // deck. Guests hit the signup gate.
  const save = {
    saved: savedIds.has(listing.id),
    onToggle: () => {
      if (!user) {
        onRequireAuth?.();
        return;
      }
      toggleSave(listing);
    },
  };

  return (
    <ExpandShell
      onClose={onClose}
      // WHY: the popup snapshots its listing at open time, so its buttons
      // must act on THAT listing. Threading it through lets the deck verify
      // the shown listing is still the top card before swiping, otherwise a
      // deck advance would make Like or Pass hit the wrong home.
      onAction={(action) => onAction?.(action, listing)}
      media={media}
      initialSlide={initialSlide}
      testId="listing-expand"
      save={save}
    >
      {/* Price / title / location now live BELOW the photo (dark text on the
          light sheet) so the image itself is never covered by text. */}
      <p className="text-[28px] font-bold leading-none tracking-tight text-brand-ink">
        ${parts.amount.toLocaleString()}
        <span className="text-base font-medium text-brand-secondary"> {parts.unit}</span>
      </p>
      <h2 className="mt-1.5 text-lg font-semibold leading-snug text-brand-ink">{listing.title}</h2>
      {/* "City, PROVINCE" (geographic comma label), matching the cards. */}
      <p className="mt-0.5 text-sm text-brand-secondary">
        {[listing.neighborhood, listing.city, listing.province].filter(Boolean).join(", ")}
        {listing.distance_km != null && <> · {listing.distance_km} km away</>}
      </p>

      {/* Badges. The lister's photo lives in the "Listed by" block below. */}
      {(listing.verified || listing.lister_type === "realtor") && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {listing.verified && <BadgeVerified />}
          <ListerBadge type={listing.lister_type} />
        </div>
      )}

      {/* Icon info chips */}
      <div data-testid="expand-rows" className="mt-3 flex flex-wrap gap-2">
        <Chip icon={BedDouble}>{listing.beds === 0 ? "Studio" : `${listing.beds} bed`}{listing.dens ? ` + ${listing.dens} den` : ""}</Chip>
        <Chip icon={Bath}>{listing.baths} bath</Chip>
        {listing.sqft ? <Chip icon={Ruler}>{listing.sqft} sqft</Chip> : null}
        <Chip icon={PawPrint}>{listing.pets ? "Pets welcome" : "No pets"}</Chip>
        {listing.smoking ? <Chip icon={Cigarette}>Smoking allowed</Chip> : <Chip icon={CigaretteOff}>No smoking</Chip>}
        {hasAmenity(/laundry|washer/i) && <Chip icon={WashingMachine}>In suite laundry</Chip>}
        {hasAmenity(/parking|garage/i) && <Chip icon={Car}>Parking</Chip>}
        {hasAmenity(/heat|hydro|utilit/i) && <Chip icon={Zap}>Utilities included</Chip>}
        {listing.available_from && <Chip icon={CalendarDays}>Available {fmtDate(listing.available_from)}</Chip>}
      </div>

      {/* Description (plain text here — rich HTML renders on the full page) */}
      {listing.description && (
        <p className="mt-4 text-sm leading-relaxed text-night-sub">
          {plainDescription(listing.description)}
        </p>
      )}

      {/* Amenity chips: proper labels + icons, never raw snake_case keys */}
      {collectAmenities(listing).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {collectAmenities(listing).slice(0, 14).map((a) => {
            const Icon = iconFor(a);
            return (
              <span
                key={a}
                className="inline-flex items-center gap-1.5 rounded-full border border-night-line bg-night px-3 py-1.5 text-xs font-semibold text-night-text"
              >
                <Icon size={13} strokeWidth={2} className="shrink-0 text-brand-primary" />
                {labelFor(a)}
              </span>
            );
          })}
        </div>
      )}

      {/* Lister attribution: headshot avatar + name + brokerage. Tapping it
          opens all of this lister's listings (/hosts/:id). */}
      {(listing.lister_name || listing.lister_photo) && (
        <Link
          to={listing.owner_id ? `/hosts/${listing.owner_id}` : "#"}
          className="mt-4 flex items-center gap-3 rounded-xl border-t border-night-line pt-4 transition hover:opacity-90"
        >
          {listing.lister_photo ? (
            <img
              src={listing.lister_photo}
              alt={listing.lister_name || "Lister"}
              loading="lazy"
              decoding="async"
              className="h-10 w-10 shrink-0 rounded-full bg-night object-cover ring-1 ring-night-line"
            />
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-primary text-sm font-semibold text-white">
              {listing.lister_name?.slice(0, 1)}
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm text-night-sub">
              Listed by <span className="font-semibold text-night-text">{listing.lister_name}</span>
            </p>
            {(listing.lister_brokerage || listing.courtesy_of) && (
              <p className="truncate text-xs text-night-sub">
                {listing.lister_brokerage || listing.courtesy_of}
              </p>
            )}
            <p className="mt-0.5 truncate text-xs font-semibold text-brand-lime">See all their listings →</p>
          </div>
        </Link>
      )}
      <Link
        to={`/listing/${listing.id}`}
        className="mt-3 inline-block text-sm font-semibold text-brand-primary underline underline-offset-2 hover:text-brand-primary-hover"
      >
        View full listing
      </Link>
    </ExpandShell>
  );
}
